import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Smartphone, Share, PlusSquare, Copy, MoreVertical, Download } from "lucide-react";

const isLocal = () => /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

export function useAppLink() {
  const { data } = useQuery({ queryKey: ["app-link"], queryFn: async () => (await api.get("/api/app/link")).data, staleTime: 60000 });
  // On a public address the page's own origin is the link; on the Mac it's the one learned or set by the admin
  return isLocal() ? data?.url || null : window.location.origin;
}

export function QR({ text, size = 220 }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!text) return;
    QRCode.toDataURL(text, { width: size * 2, margin: 1, color: { dark: "#050505", light: "#FFFFFF" }, errorCorrectionLevel: "M" })
      .then(setSrc).catch(() => setSrc(""));
  }, [text, size]);
  if (!src) return <div style={{ width: size, height: size }} className="rounded-2xl bg-[#1E222A] animate-pulse" />;
  return (
    <div className="relative rounded-2xl bg-white p-3 shadow-[0_0_60px_rgba(204,255,0,0.25)]" style={{ width: size + 24 }}>
      <img src={src} alt="QR code to open OCTON Gym" width={size} height={size} />
      <img src="/apple-touch-icon.png" alt="" className="absolute rounded-lg border-4 border-white" style={{ width: size * 0.2, height: size * 0.2, left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} />
    </div>
  );
}

function Steps() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <div className="rounded-xl border border-[#1E222A] p-3">
        <p className="font-heading font-bold mb-2">iPhone</p>
        <ol className="space-y-1.5 text-[#B7BECB]">
          <li>1. Point the camera at the code and tap the link (it opens in Safari).</li>
          <li className="flex items-center gap-1.5 flex-wrap">2. Tap <Share className="w-4 h-4 text-acid" /> Share at the bottom.</li>
          <li className="flex items-center gap-1.5 flex-wrap">3. Tap <PlusSquare className="w-4 h-4 text-acid" /> Add to Home Screen, then Add.</li>
        </ol>
      </div>
      <div className="rounded-xl border border-[#1E222A] p-3">
        <p className="font-heading font-bold mb-2">Android</p>
        <ol className="space-y-1.5 text-[#B7BECB]">
          <li>1. Scan the code with the camera and open the link in Chrome.</li>
          <li className="flex items-center gap-1.5 flex-wrap">2. Tap <Download className="w-4 h-4 text-acid" /> Install when it appears, or <MoreVertical className="w-4 h-4 text-acid" /> menu.</li>
          <li>3. Choose Install app (or Add to Home screen).</li>
        </ol>
      </div>
    </div>
  );
}

function SetLink() {
  const qc = useQueryClient();
  const [v, setV] = useState("");
  const save = async () => {
    try { await api.post("/api/admin/app-link", { url: v }); toast.success("Phone link saved"); qc.invalidateQueries({ queryKey: ["app-link"] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="flex gap-2">
      <input className="flex-1 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" placeholder="https://… (your public app link)" value={v} onChange={(e) => setV(e.target.value)} />
      <button onClick={save} className="h-10 px-4 rounded-full bg-acid text-black text-sm font-medium">Save</button>
    </div>
  );
}

// The "Get the app" dialog: scan, open, add to home screen
export function GetTheAppDialog({ open, onOpenChange, path = "", title = "Get OCTON Gym on your phone", note }) {
  const { user } = useAuth();
  const base = useAppLink();
  const link = base ? `${base}${path}` : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface border-[#1E222A] max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <div className="space-y-4" data-testid="get-the-app">
          {link ? (
            <div className="flex flex-col items-center gap-3">
              <QR text={link} />
              {note && <p className="text-sm text-acid text-center">{note}</p>}
              <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }}
                className="inline-flex items-center gap-2 text-xs text-[#8A91A6] hover:text-white break-all"><Copy className="w-3.5 h-3.5 shrink-0" /> {link}</button>
            </div>
          ) : (
            <div className="rounded-xl border border-[#FFB340]/40 bg-[#FFB340]/5 p-4 text-sm space-y-3">
              <p>The QR code needs the app's public address, and none is set yet.</p>
              {user?.role === "admin" ? (
                <>
                  <p className="text-[#B7BECB]">Start the secure phone link on the Mac and open it once in any browser (it's picked up automatically), or paste it here:</p>
                  <SetLink />
                </>
              ) : <p className="text-[#B7BECB]">Ask your club for the link to OCTON Gym.</p>}
            </div>
          )}
          <Steps />
          <p className="text-xs text-[#565C6F] text-center">It installs like an app: its own icon, full screen, no App Store needed.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function GetTheAppButton({ className = "", label = "Get the app" }) {
  const [open, setOpen] = useState(false);
  const standalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone);
  if (standalone) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="get-app-btn" className={className}>
        <Smartphone className="w-4 h-4" /> {label}
      </button>
      <GetTheAppDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
